import { useDispatch, useSelector } from "react-redux";
import { login } from "./authSlice";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    AlertCircle,
    Loader2,
    Sun,
    Moon,
    Boxes,
    MapPin,
    ClipboardCheck
} from "lucide-react";

import { useTheme } from "../../theme/ThemeContext";

const HIGHLIGHTS = [
    { icon: MapPin, title: "Lokasi rak real-time", text: "Pantau slot terisi dan kosong di setiap warehouse." },
    { icon: Boxes, title: "Master palet & rak", text: "Kelola label, kapasitas, dan penempatan palet." },
    { icon: ClipboardCheck, title: "Rekonsiliasi stok", text: "Bandingkan stok sistem dengan hasil hitung fisik." }
];

/* motif grid rak di panel brand: 6 x 8 slot, sebagian "terisi" */
const RACK_SLOTS = Array.from({ length: 48 }, (_, i) => (i * 7 + 3) % 5 !== 0 && (i * 11) % 9 > 2);

function BrandPanel() {
    return (
        <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-[#1AA3FA] to-[#0A5FE6] dark:from-[#123B6E] dark:to-[#0A1F3F] text-white p-12">
            {/* motif grid rak */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 -bottom-10 grid grid-cols-8 gap-2 rotate-[-8deg] opacity-[0.18]"
            >
                {RACK_SLOTS.map((filled, i) => (
                    <span
                        key={i}
                        className={`w-11 h-8 rounded-[5px] border border-white ${filled ? "bg-white" : ""}`}
                    />
                ))}
            </div>

            <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 rounded-[11px] bg-white flex items-center justify-center shadow-sm">
                    <span className="text-[#0A6CF0] font-bold leading-none">W</span>
                </div>
                <div className="leading-tight">
                    <div className="font-bold tracking-tight">WMS Admin</div>
                    <div className="text-xs text-white/75">Warehouse Management</div>
                </div>
            </div>

            <div className="relative max-w-md">
                <h2 className="text-[32px] leading-[1.15] font-semibold tracking-tight">
                    Semua rak, palet, dan stok dalam satu layar.
                </h2>
                <ul className="mt-8 space-y-5">
                    {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
                        <li key={title} className="flex gap-3.5">
                            <span className="w-9 h-9 rounded-lg bg-white/15 ring-1 ring-white/25 flex items-center justify-center shrink-0">
                                <Icon size={18} />
                            </span>
                            <div>
                                <div className="text-sm font-semibold">{title}</div>
                                <div className="text-sm text-white/75">{text}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="relative text-xs text-white/60">
                © {new Date().getFullYear()} WMS Admin
            </div>
        </div>
    );
}

function Field({ id, label, icon: Icon, children, trailing }) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-body mb-1.5">
                {label}
            </label>
            <div className="relative">
                <Icon
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                    aria-hidden="true"
                />
                {children}
                {trailing}
            </div>
        </div>
    );
}

const inputClass =
    "w-full h-11 rounded-lg border border-line bg-card text-body pl-10 pr-3 text-sm " +
    "placeholder:text-muted/70 transition-colors " +
    "focus:outline-none focus:border-ink focus:ring-4 focus:ring-ink/15 " +
    "disabled:opacity-60";

export default function LoginPage() {
    const dispatch = useDispatch();
    const { token, loading, error } = useSelector((state) => state.auth);
    const { isDark, toggleTheme } = useTheme();

    const [form, setForm] = useState({
        email: "",
        password: "",
    });
    const [showPassword, setShowPassword] = useState(false);

    if (token) return <Navigate to="/admin/dashboard" />;

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(login(form));
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-surface">
            <BrandPanel />

            <div className="relative flex flex-col items-center justify-center px-5 py-10 sm:px-8">
                <button
                    type="button"
                    onClick={toggleTheme}
                    className="absolute top-4 right-4 p-2 rounded-md text-muted hover:bg-card hover:text-body"
                    title={isDark ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
                    aria-label={isDark ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
                >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                <div className="w-full max-w-[400px]">
                    {/* logo untuk layar kecil (panel brand disembunyikan) */}
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-[11px] bg-gradient-to-br from-[#1AA3FA] to-[#0A5FE6] flex items-center justify-center">
                            <span className="text-white font-bold leading-none">W</span>
                        </div>
                        <div className="leading-tight">
                            <div className="font-bold tracking-tight text-body">WMS Admin</div>
                            <div className="text-xs text-muted">Warehouse Management</div>
                        </div>
                    </div>

                    <div className="bg-card border border-line rounded-2xl shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-12px_rgba(16,24,40,0.12)] p-6 sm:p-8">
                        <h1 className="text-2xl font-semibold tracking-tight text-body">Masuk</h1>
                        <p className="mt-1 text-sm text-muted">
                            Gunakan akun admin untuk mengelola warehouse.
                        </p>

                        {error && (
                            <div
                                role="alert"
                                className="mt-5 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
                            >
                                <AlertCircle size={17} className="mt-px shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                            <Field id="email" label="Email" icon={Mail}>
                                <input
                                    id="email"
                                    type="email"
                                    autoComplete="username"
                                    autoFocus
                                    required
                                    placeholder="nama@perusahaan.com"
                                    value={form.email}
                                    disabled={loading}
                                    className={inputClass}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </Field>

                            <Field
                                id="password"
                                label="Password"
                                icon={Lock}
                                trailing={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted hover:text-body"
                                        aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                                        title={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                                    >
                                        {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                                    </button>
                                }
                            >
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    placeholder="Masukkan password"
                                    value={form.password}
                                    disabled={loading}
                                    className={`${inputClass} pr-11`}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                />
                            </Field>

                            <button
                                type="submit"
                                disabled={loading || !form.email || !form.password}
                                className="w-full h-11 mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-ink text-white text-sm font-semibold shadow-sm transition-colors hover:bg-ink-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-ink/25 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading && <Loader2 size={17} className="animate-spin" />}
                                {loading ? "Memproses..." : "Masuk"}
                            </button>
                        </form>
                    </div>

                    <p className="mt-6 text-center text-xs text-muted">
                        Lupa password? Hubungi administrator sistem.
                    </p>
                </div>
            </div>
        </div>
    );
}
